import { createSlice } from '@reduxjs/toolkit';
import {
  getAnalyticsReservationListData,
  getTotalReservationsStats,
  getAnalyticsSpaAppointmentsList,
  getAnalyticsTotalSpaAppointment,
  getAnalyticsMostBookedSpaTreatment,
  getAnalyticsTotalHKRequests,
  getHKAnalyticsServiceDetailsData,
  getTotalIRDOrdersDetail,
  getDigitalCheckInStats,
  getAnalyticsUsageState,
  getTopIrdOrderItems,
  getHKAnalyticsServiceDetailsFilterData,
  listHKAnalyticsServiceActivityData,
  getTopIrdOrderItemsFilter,
  listItemActivityData,
  listUpsellItemActivityData,
  listSPAAnalyticsServiceActivityData,
} from '../../utils/analyticsAPIUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../utils/loaderSlice';
import { notificationManager } from '../../utils/NotificationUtils';
import { logError } from '../../utils/commonUtils';
import { ENVIRONMENT } from '../../env/Configurations';

export const analyticsSlice = createSlice({
  name: 'analytics',
  initialState: {
    restaurantReservationList: [],
    totalReservationStats: {},
    spaAppointmentList: [],
    totalSpaAppointmentStats: {},
    mostBookedTreatments: [],
    totalReservationStats: {},
    totalHkRequests: {},
    hkServiceDetails: {},
    totalIRDOrderDetails: {},
    digitalCheckInState: {},
    appUsage: {},
    topIrdItems: {},
    hkServiceFilterDetails: {},
    hkActivityList: [],
    topIrdItemsFilter: {},
    irdActivityList: [],
    irdUpsellActivityList: [],
    listSPAServiceActivity: [],
  },
  reducers: {
    setAnalyticsReservationList: (state, action) => {
      state.restaurantReservationList = action.payload;
    },
    setTotalReservationStats: (state, action) => {
      state.totalReservationStats = action.payload;
    },
    setAnalyticsSpaAppointmentList: (state, action) => {
      state.spaAppointmentList = action.payload;
    },
    setTotalSpaAppointmentsStats: (state, action) => {
      state.totalSpaAppointmentStats = action.payload;
    },
    setMostBookedTreatments: (state, action) => {
      state.mostBookedTreatments = action.payload;
    },
    setTotalHkRequests: (state, action) => {
      state.totalHkRequests = action.payload;
    },
    setHkServiceDetails: (state, action) => {
      state.hkServiceDetails = action.payload;
    },
    setHkServiceFilterDetails: (state, action) => {
      state.hkServiceFilterDetails = action.payload;
    },
    setTotalIrdOrders: (state, action) => {
      state.totalIRDOrderDetails = action.payload;
    },
    setDigitalCheckingState: (state, action) => {
      state.digitalCheckInState = action.payload;
    },
    setAppUsageState: (state, action) => {
      state.appUsage = action.payload;
    },
    setTopIrdItems: (state, action) => {
      state.topIrdItems = action.payload;
    },
    setHKActivityList: (state, action) => {
      state.hkActivityList = action.payload;
    },
    setTopIrdItemsFilter: (state, action) => {
      state.topIrdItemsFilter = action.payload;
    },
    setIRDActivityList: (state, action) => {
      state.irdActivityList = action.payload;
    },
    setIRDUpsellActivityList: (state, action) => {
      state.irdUpsellActivityList = action.payload;
    },
    setSpaActivityList: (state, action) => {
      state.listSPAServiceActivity = action.payload;
    },

    setInitialAppUsage: (state, action) => {
      state.appUsage = {};
    },
  },
});

export const {
  setAnalyticsReservationList,
  setTotalReservationStats,
  setAnalyticsSpaAppointmentList,
  setTotalSpaAppointmentsStats,
  setMostBookedTreatments,
  setTotalHkRequests,
  setHkServiceDetails,
  setTotalIrdOrders,
  setDigitalCheckingState,
  setAppUsageState,
  setTopIrdItems,
  setHkServiceFilterDetails,
  setHKActivityList,
  setTopIrdItemsFilter,
  setIRDActivityList,
  setIRDUpsellActivityList,
  setSpaActivityList,
  setInitialAppUsage,
} = analyticsSlice.actions;

export const fetchRestaurantReservationList = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getAnalyticsReservationListData(payload);
    const data = res.data.getAnalyticsReservationList;
    dispatch(setAnalyticsReservationList(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchRestaurantReservationList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchTotalReservationsStats = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getTotalReservationsStats(payload);
    const data = res.data.getTotalReservationStats;
    dispatch(setTotalReservationStats(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchTotalReservationsStats', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAnalyticSpaAppointmentList = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getAnalyticsSpaAppointmentsList(payload);
    const data = res.data.getAnalyticsSpaAppointmentList;
    dispatch(setAnalyticsSpaAppointmentList(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchAnalyticSpaAppointmentList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchTotalSpaAppointmentsStats = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getAnalyticsTotalSpaAppointment(payload);
    const data = res.data.getAnalyticsTotalSpaAppointments;
    dispatch(setTotalSpaAppointmentsStats(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchTotalSpaAppointmentsStats', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchMostBookedTreatments = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getAnalyticsMostBookedSpaTreatment(payload);
    let data = res.data.getAnalyticsMostBookedSpaTreatments;
    dispatch(setMostBookedTreatments(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchMostBookedTreatments', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchTotalHkRequests = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getAnalyticsTotalHKRequests(payload);
    const data = res.data.getAnalyticsTotalHKRequest;
    dispatch(setTotalHkRequests(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchTotalHkRequests', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchHKServiceDetails = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getHKAnalyticsServiceDetailsData(payload);
    const data = res.data.getHKAnalyticsServiceDetails;
    dispatch(setHkServiceDetails(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchHKServiceDetails', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchHKServiceFilterDetails = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getHKAnalyticsServiceDetailsFilterData(payload);
    const data = res.data.getHKAnalyticsServiceDetails;
    dispatch(setHkServiceFilterDetails(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchHKServiceFilterDetails', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchHKServiceActivityDetailsList = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await listHKAnalyticsServiceActivityData(payload);
    const data = res.data.listHKAnalyticsServiceActivity;
    dispatch(setHKActivityList(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchHKServiceActivityDetailsList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchTotalIRDOrderDetails = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getTotalIRDOrdersDetail(payload);
    const data = res.data.getTotalIRDOrderDetails;
    dispatch(setTotalIrdOrders(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchTotalIRDOrderDetails', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchDigitalCheckInDetails = (payload) => async (dispatch) => {
  try {
    const res = await getDigitalCheckInStats(payload);
    const data = res.data.getAnalyticsCheckInStats;
    dispatch(setDigitalCheckingState(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchDigitalCheckInDetails', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  }
};

export const fetchAppUsageDetails = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getAnalyticsUsageState(payload);
    const data = res.data.getAnalyticsUsageStats;
    dispatch(setAppUsageState(data));
  } catch (error) {
    dispatch(setAppUsageState({ totalHits: {}, userStats: {}, sessionStats: {} }));
    logError(payload?.['hotelId'], 'fetchAppUsageDetails', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const setInitialAppStatusData = () => async (dispatch) => {
  dispatch(setInitialAppUsage());
};

export const fetchTopIrdItems = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getTopIrdOrderItems(payload);
    const data = res.data.getTopIrdItems;
    dispatch(setTopIrdItems(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchTopIrdItems', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchTopIrdItemsFilter = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getTopIrdOrderItemsFilter(payload);
    const data = res.data.getTopIrdItems;
    dispatch(setTopIrdItemsFilter(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchTopIrdItemsFilter', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchIrdActivityList = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await listItemActivityData(payload);
    const data = res.data.listItemActivity;
    dispatch(setIRDActivityList(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchIrdActivityList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchIrdUpsellActivityList = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await listUpsellItemActivityData(payload);
    const data = res.data.listItemActivity;
    dispatch(setIRDUpsellActivityList(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchIrdUpsellActivityList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchSpaServiceActivityList = (payload) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await listSPAAnalyticsServiceActivityData(payload);
    const data = res.data.listSPAAnalyticsServiceActivity;
    dispatch(setSpaActivityList(data));
  } catch (error) {
    logError(payload?.['hotelId'], 'fetchSpaServiceActivityList', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const analyticsReservationListSelector = (state) =>
  state.analytics.restaurantReservationList;
export const totalReservationsStatsSelector = (state) => state.analytics.totalReservationStats;
export const analyticsSpaAppointmentListSelector = (state) => state.analytics.spaAppointmentList;
export const totalSpaAppointmentsStatsSelector = (state) =>
  state.analytics.totalSpaAppointmentStats;
export const mostBookedTreatmentsSelector = (state) => state.analytics.mostBookedTreatments;
export const totalHkRequestsSelector = (state) => state.analytics.totalHkRequests;
export const hkServiceDetailsSelector = (state) => state.analytics.hkServiceDetails;
export const totalIrdOrdersSelector = (state) => state.analytics.totalIRDOrderDetails;
export const digitalCheckInSelector = (state) => state.analytics.digitalCheckInState;
export const appUsageSelector = (state) => state.analytics.appUsage;
export const topIrdItemsSelector = (state) => state.analytics.topIrdItems;
export const hkServiceDetailsFilterSelector = (state) => state.analytics.hkServiceFilterDetails;
export const hkActivityListSelector = (state) => state.analytics.hkActivityList;
export const topIrdItemsFilterSelector = (state) => state.analytics.topIrdItemsFilter;
export const irdActivityListSelector = (state) => state.analytics.irdActivityList;
export const irdUpsellActivityListSelector = (state) => state.analytics.irdUpsellActivityList;
export const spaActivityListSelector = (state) => state.analytics.listSPAServiceActivity;

export default analyticsSlice.reducer;
